// #region IMPORTS
import * as THREE from 'three';

// #endregion

// #region SUPPORT
export type ThreeCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

export enum ViewPlacement {
  None = 0, // Disable Auto Placement, manually set top & left coordinate
  Center = 1,
  TopLeft = 2,
  TopRight = 3,
  BottomRight = 4,
  BottomLeft = 5,
}
// #endregion

export class ViewportRenderer {
  // #region MAIN
  #width: number = 200; // Size of the viewport
  #height: number = 200;
  #left: number = 0; // Top-Left cordinate for the view port
  #top: number = 0;
  #canvasWidth: number = 0; // Store canvas size to avoid keeping reference to renderer
  #canvasHeight: number = 0;
  #padding: number = 0; // How much padded space to add to placement
  #placement: ViewPlacement = ViewPlacement.TopLeft;
  #zoomScale: number = 1; // Scale the zoom value
  #initZoom: number = 1; // Starting zoom value from main camera
  #camera!: ThreeCamera; // Internal camer for viewport rendering
  #ndcPointer?: THREE.Vector2; // Used to position camera when following with orthographic camera

  constructor(props?: {
    width?: number;
    height?: number;
    padding?: number;
    zoom?: number;
    placement?: ViewPlacement;
  }) {
    if (props?.width && props?.height) {
      this.#width = props.width;
      this.#height = props.height;
    }

    if (props?.padding) this.#padding = props.padding;
    if (props?.zoom) this.#zoomScale = props.zoom;
    if (props?.placement !== undefined) this.#placement = props.placement;

    this.#updateCameraMatrix();
    this.#updatePlacement();
  }
  // #endregion

  // #region SETTERS
  setZoom(v: number): this {
    this.#zoomScale = v;
    this.#updateCameraMatrix();
    return this;
  }

  // set the width & height of the viewport
  setSize(w: number, h: number): this {
    this.#width = w;
    this.#height = h;
    this.#updateCameraMatrix();
    this.#updatePlacement();
    return this;
  }

  setCanvasSize(w: number, h: number): this {
    this.#canvasWidth = Math.round(w);
    this.#canvasHeight = Math.round(h);
    this.#updatePlacement();
    return this;
  }

  setPlacement(v: ViewPlacement): this {
    this.#placement = v;
    this.#updatePlacement();
    return this;
  }

  // Top-Left pixe; cordinate for viewport
  setPosition(x: number, y: number): this {
    this.#top = y;
    this.#left = x;
    return this;
  }

  // Pixel padding away from canvas edges when using placement
  setPadding(v: number) {
    this.#padding = v;
    this.#updatePlacement();
    return this;
  }

  // Normalized Device Coordinate space of the mouse position
  setNdcPointer(v?: THREE.Vector2): this {
    this.#ndcPointer = v;
    return this;
  }

  // Use mouse position to set NDC Pointer
  setPointer(v?: THREE.Vector2): this {
    // Convert mouse position to normalized device coordinates ( -1 to 1 )
    this.#ndcPointer = v
      ? new THREE.Vector2(
          (v.x / this.#canvasWidth) * 2 - 1,
          -(v.y / this.#canvasHeight) * 2 + 1,
        )
      : undefined;
    return this;
  }
  // #endregion

  // #region GETTERS

  getSize(): [number, number] {
    return [this.#width, this.#height];
  }

  getPosition(): [number, number] {
    return [this.#left, this.#top];
  }

  // #endregion

  // #region MANAGE INTERNAL CAMERA

  // Clone & prepare internal camera
  #initCamera(cam: ThreeCamera) {
    switch (cam.type) {
      case 'PerspectiveCamera':
        this.#camera = new THREE.PerspectiveCamera();
        break;
      case 'OrthographicCamera':
        this.#camera = new THREE.OrthographicCamera();
        break;
    }

    this.#initZoom = cam.zoom;
    this.#updateCameraMatrix();
  }

  // Update camera with sizes & regen its projection matrix
  #updateCameraMatrix() {
    if (this.#camera instanceof THREE.PerspectiveCamera) {
      this.#camera.aspect = this.#width / this.#height;
      this.#camera.zoom = this.#initZoom * this.#zoomScale;
    } else if (this.#camera instanceof THREE.OrthographicCamera) {
      this.#camera.left = this.#width / -0.6;
      this.#camera.right = this.#width / 0.6;
      this.#camera.top = this.#height / 0.6;
      this.#camera.bottom = this.#height / -0.6;
      this.#camera.zoom = this.#initZoom * this.#zoomScale;
    }

    this.#camera?.updateProjectionMatrix();
  }

  // If not none, update the left & top coordinates of the view
  #updatePlacement(): this {
    const pad = this.#padding;

    switch (this.#placement) {
      case ViewPlacement.TopLeft:
        return this.setPosition(pad, pad);
      case ViewPlacement.TopRight:
        return this.setPosition(this.#canvasWidth - this.#width - pad, pad);
      case ViewPlacement.BottomRight:
        return this.setPosition(
          this.#canvasWidth - this.#width - pad,
          this.#canvasHeight - this.#height - pad,
        );
      case ViewPlacement.BottomLeft:
        return this.setPosition(pad, this.#canvasHeight - this.#height - pad);
      case ViewPlacement.Center:
        return this.setPosition(
          Math.round(this.#canvasWidth * 0.5 - this.#width * 0.5),
          Math.round(this.#canvasHeight * 0.5 - this.#height * 0.5),
        );
    }

    return this;
  }

  // Compute the 3D position of the 2D mouse in the camera's near plane
  #getNearPlanePosition(ndc: THREE.Vector2, cam: ThreeCamera) {
    // Z = -1 to project onto near plane, use 1 for far plane
    const pos = new THREE.Vector3(ndc.x, ndc.y, -1);
    pos.unproject(cam);
    return pos;
  }

  // Compute the 3D position of the 2D mouse in the camera's far plane
  #getFarPlanePosition(ndc: THREE.Vector2, cam: ThreeCamera) {
    // Z = -1 to project onto near plane, use 1 for far plane
    const pos = new THREE.Vector3(ndc.x, ndc.y, 1);
    pos.unproject(cam);
    return pos;
  }
  // #endregion

  // #region RENDERING
  render(
    scene: THREE.Scene,
    mainCamera: ThreeCamera,
    renderer: THREE.WebGLRenderer,
  ) {
    // const screen = renderer.getDrawingBufferSize( new Vector2() );
    const screen = renderer.getSize(new THREE.Vector2());

    // Convert between TOP-LEFT origin to BOTTOM-LEFT origin
    const invTop = screen.y - this.#top - this.#height;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // PREPARE CAMERA

    // NOTES: Reason for late initializing of inner camera
    // Fiber/React have delays in execution. During initialization of react com
    // the camera can be undefined at times. Then there is the usecase of
    // switching the default to orthographic which results in having perspective
    // in the initialization then ortho during render.
    if (!this.#camera || this.#camera.type !== mainCamera.type) {
      this.#initCamera(mainCamera);
    }

    // To follow under the mouse with orthographic, need mouse NDC
    // to reposition the camera at that relative & coplanar to the main camera
    if (this.#camera.type === 'OrthographicCamera' && this.#ndcPointer) {
      this.#camera.position.copy(
        this.#getNearPlanePosition(this.#ndcPointer, mainCamera),
      );
    } else {
      this.#camera.position.copy(mainCamera.position);
    }

    if (this.#camera.type === 'PerspectiveCamera' && this.#ndcPointer) {
      // Reorient the camera to point toward the raycaster direction
      // target_point = camera.pos + norm( ray.dir )
      const dirLook = this.#getFarPlanePosition(this.#ndcPointer, mainCamera)
        .sub(mainCamera.position)
        .normalize();

      const pntLook = mainCamera.position.clone().add(dirLook);
      this.#camera.lookAt(pntLook);
    } else {
      this.#camera.quaternion.copy(mainCamera.quaternion);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Set Renderer
    renderer.setScissorTest(true);
    renderer.setScissor(this.#left, invTop, this.#width, this.#height);
    renderer.setViewport(this.#left, invTop, this.#width, this.#height);
    renderer.render(scene, this.#camera);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Cleanup
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, screen.x, screen.y);
  }
  // #endregion
}
